import { useEffect, useState } from "react";
import { Check, Copy, Loader2, Settings, X } from "lucide-react";
import type { BuildPath, BuildSetupResult } from "../hooks/useInitiativeAssist";

type Props = {
  title: string;
  path?: BuildPath;
  loading: boolean;
  error: string | null;
  setup: BuildSetupResult | null;
  onRetry: () => void;
  onClose: () => void;
};

const PATH_LABELS: Record<BuildPath, string> = {
  citizen: "AI Builder",
  hybrid: "Hybrid",
  engineer: "Engineer needed",
};

const KIND_LABELS: Record<BuildSetupResult["snippets"][number]["kind"], string> = {
  prompt: "Paste",
  code: "Code",
  command: "Terminal",
};

/** AI-drafted "how to configure it" popup: the best starting tool, ordered
 *  setup steps, and ready-to-copy snippets. Presentational only — the parent
 *  owns fetching and caching. */
export function BuildSetupPopup({ title, path, loading, error, setup, onRetry, onClose }: Props) {
  const [copied, setCopied] = useState<number | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const copy = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(index);
      window.setTimeout(() => setCopied((c) => (c === index ? null : c)), 1600);
    } catch {
      /* clipboard blocked — the text is still selectable on screen */
    }
  };

  return (
    <div
      className="modal-backdrop guide-backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="modal guide-modal guide-modal--wide"
        role="dialog"
        aria-modal="true"
        aria-label="How to configure"
      >
        <div className="guide-head">
          <span className="guide-icon" aria-hidden>
            <Settings size={18} />
          </span>
          <div className="guide-head-text">
            <span className="guide-kind">How to configure</span>
            <h3 className="guide-title">{title || "Untitled initiative"}</h3>
            <span className="guide-tags">
              {setup?.tool ? <span className="guide-tool">{setup.tool}</span> : null}
              {path ? (
                <span className={`guide-path guide-path--${path}`}>{PATH_LABELS[path]}</span>
              ) : null}
            </span>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="guide-body">
          {loading ? (
            <div className="guide-loading">
              <Loader2 size={16} className="add-item-spin" /> Drafting setup instructions…
            </div>
          ) : error ? (
            <div className="guide-error">
              <p>{error}</p>
              <button type="button" className="btn btn-ghost" onClick={onRetry}>
                Try again
              </button>
            </div>
          ) : setup ? (
            <>
              {setup.why ? <p className="guide-overview">{setup.why}</p> : null}
              <ol className="guide-steps">
                {setup.steps.map((s, i) => (
                  <li key={i}>
                    <span className="guide-step-num">{i + 1}</span>
                    <span>
                      <b>{s.title}</b>
                      {s.detail ? <em>{s.detail}</em> : null}
                    </span>
                  </li>
                ))}
              </ol>

              {setup.snippets.map((sn, i) => (
                <div key={i} className="guide-snippet">
                  <div className="guide-snippet-head">
                    <span className="guide-snippet-label">
                      <span className="guide-snippet-kind">{KIND_LABELS[sn.kind]}</span>
                      {sn.label}
                    </span>
                    <button
                      type="button"
                      className="guide-copy-btn"
                      onClick={() => copy(sn.content, i)}
                    >
                      {copied === i ? (
                        <>
                          <Check size={12} /> Copied
                        </>
                      ) : (
                        <>
                          <Copy size={12} /> Copy
                        </>
                      )}
                    </button>
                  </div>
                  <pre className="guide-snippet-body">{sn.content}</pre>
                </div>
              ))}

              <p className="guide-note">
                AI-drafted starting point only — adjust it to your environment and confirm the
                approach with IT and Security before building.
              </p>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
